import { computed, ref, watch } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { useDebounceFn, useEventListener } from '@vueuse/core';
import { useAlert } from 'dashboard/composables';
import ConversationApi from 'dashboard/api/inbox/conversation';

// MakerHub: "Pin to left menu" — per-user sidebar shortcuts to specific
// conversations. DISTINCT from the WhatsApp-style list pin in
// usePinnedConversations.js (that one floats a chat to the top of the chat
// list, per browser); this one adds a navigation entry under Conversations
// in the left menu, synced across devices.
//
// Persistence = users.custom_attributes.sidebar_pinned_conversations via the
// profile endpoint's key-scoped custom_attributes merge — deliberately NOT
// ui_settings, whose whole-blob PUTs from stale tabs would clobber pins.
// Shape, keyed by account id (custom_attributes is global per user):
//   { "3": [{ id: <display_id>, label: null|string, title: <name at pin> }] }
// Array order = display order. label = custom rename (null → live name).
// title = contact/group name snapshot at pin time, used as a cold-start /
// deleted-conversation fallback; refreshed only on pin actions.

export const MAX_SIDEBAR_PINS = 10;
const PINS_KEY = 'sidebar_pinned_conversations';

// Pins whose hydration hit an authoritative access failure (403) or exhausted
// its retries — the leaf renders them muted with no badge. Module-level so the
// hydrator (Sidebar) and every leaf share one reactive set.
const unavailablePinIds = ref(new Set());

// Shared "manage pin" menu state — a single menu instance (SidebarPinMenu.vue,
// mounted once in Sidebar.vue) serves every leaf's kebab and right-click.
const pinMenuState = ref(null); // { id, x, y } | null

export function useSidebarPinMenu() {
  const openPinMenu = (id, event) => {
    let x = event.pageX || event.clientX;
    let y = event.pageY || event.clientY;
    // Keyboard-activated clicks carry (0,0) — anchor to the trigger instead
    // so the menu doesn't open at the viewport corner.
    if (!x && !y && event.currentTarget?.getBoundingClientRect) {
      const rect = event.currentTarget.getBoundingClientRect();
      x = rect.left + rect.width / 2;
      y = rect.bottom;
    }
    pinMenuState.value = { id: Number(id), x, y };
  };

  const closePinMenu = () => {
    pinMenuState.value = null;
  };

  return { pinMenuState, openPinMenu, closePinMenu };
}

export function useSidebarPinnedConversations() {
  const store = useStore();
  const { t } = useI18n();

  const accountKey = () => String(store.getters.getCurrentAccountId);

  const pins = computed(() => {
    const byAccount =
      store.getters.getCurrentUser?.custom_attributes?.[PINS_KEY] || {};
    return byAccount[accountKey()] || [];
  });

  const isPinned = id => pins.value.some(pin => Number(pin.id) === Number(id));

  // save takes an UPDATER applied to the account's array AFTER the profile
  // refresh — deriving the next array from pre-refresh state would let a
  // stale tab overwrite pins added elsewhere (the pin-vs-pin race this
  // refresh exists to prevent). Updaters return null to abort.
  const save = async updater => {
    await store.dispatch('validityCheck');
    const byAccount =
      store.getters.getCurrentUser?.custom_attributes?.[PINS_KEY] || {};
    const nextPins = updater(byAccount[accountKey()] || []);
    if (!nextPins) return;
    try {
      await store.dispatch('updateSidebarPinnedConversations', {
        ...byAccount,
        [accountKey()]: nextPins,
      });
    } catch (error) {
      useAlert(t('SIDEBAR.PINNED.SAVE_ERROR'));
    }
  };

  const pinTitle = chat => chat?.meta?.sender?.name || `#${chat?.id}`;

  const pin = chat => {
    // Local isPinned fast-path only avoids a pointless round-trip; its worst
    // stale-state outcome is a no-op. The cap is deliberately NOT pre-checked
    // here — a stale tab showing 10 pins must not reject a pin the refreshed
    // state (checked inside the updater) would allow.
    if (isPinned(chat.id)) return;
    const entry = { id: chat.id, label: null, title: pinTitle(chat) };
    save(current => {
      if (current.some(p => Number(p.id) === Number(chat.id))) return null;
      if (current.length >= MAX_SIDEBAR_PINS) {
        useAlert(t('SIDEBAR.PINNED.LIMIT_REACHED'));
        return null;
      }
      return [...current, entry];
    });
  };

  const unpin = id =>
    save(current => current.filter(p => Number(p.id) !== Number(id)));

  const toggle = chat => {
    if (isPinned(chat.id)) unpin(chat.id);
    else pin(chat);
  };

  const rename = (id, label) => {
    const nextLabel = (label || '').trim().slice(0, 80) || null;
    save(current =>
      current.map(p =>
        Number(p.id) === Number(id) ? { ...p, label: nextLabel } : p
      )
    );
  };

  // Swap with the neighbor; fixed items (All/MakerHub/Smith3D) are not part
  // of this list, so pins can only ever swap among themselves.
  const move = (id, delta) =>
    save(current => {
      const list = [...current];
      const from = list.findIndex(p => Number(p.id) === Number(id));
      const to = from + delta;
      if (from < 0 || to < 0 || to >= list.length) return null;
      [list[from], list[to]] = [list[to], list[from]];
      return list;
    });

  return {
    pins,
    isPinned,
    pin,
    unpin,
    toggle,
    rename,
    move,
    unavailablePinIds,
  };
}

// Keeps pinned conversations present in the conversations store so their
// unread badges stay live off the existing websocket→store path. Mount ONCE
// (Sidebar.vue). The store is emptied on every list-filter switch
// (ChatList.vue) and websocket reconnect (ReconnectService), so this watches
// pin presence and re-fetches whatever went missing, instead of fetching only
// on boot.
const MAX_FETCH_FAILURES = 2;
const PRUNE_AFTER_NOT_FOUND = 2;
const RETRY_DELAY_MS = 30000;

export function useSidebarPinsHydration() {
  const store = useStore();
  const { pins, unpin } = useSidebarPinnedConversations();

  const inFlight = new Set();
  const failures = new Map(); // id -> { count, notFound }

  const markUnavailable = (id, unavailable) => {
    const next = new Set(unavailablePinIds.value);
    if (unavailable) next.add(Number(id));
    else next.delete(Number(id));
    unavailablePinIds.value = next;
  };

  // Soft reset (online / tab-visible) re-arms capped network/5xx failures but
  // keeps 403-parked pins parked — those only retry when the pin set changes
  // (includeParked). Keeping parked ids in the unavailable set also avoids a
  // muted-style flicker on refocus.
  const resetFailures = ({ includeParked = false } = {}) => {
    const keptUnavailable = new Set();
    failures.forEach((failure, id) => {
      if (failure.parked && !includeParked) keptUnavailable.add(Number(id));
      else failures.delete(id);
    });
    unavailablePinIds.value = keptUnavailable;
  };

  const missingIds = computed(() =>
    pins.value
      .map(pin => Number(pin.id))
      .filter(id => !store.getters.getConversationById(id))
  );

  // Retryable failures (first 404, transient network) get a deterministic
  // follow-up — the presence watcher alone only re-fires on unrelated store
  // mutations, which a quiet dashboard may not produce for a while. The tick
  // is consumed by a watcher below (avoids a define-order cycle with
  // hydrateMissing).
  let retryTimer = null;
  const retryTick = ref(0);
  const scheduleRetry = () => {
    if (retryTimer) return;
    retryTimer = setTimeout(() => {
      retryTimer = null;
      retryTick.value += 1;
    }, RETRY_DELAY_MS);
  };

  const hydrate = async id => {
    if (inFlight.has(id)) return;
    const failure = failures.get(id) || { count: 0, notFound: 0 };
    if (failure.count >= MAX_FETCH_FAILURES) return;
    inFlight.add(id);
    try {
      const response = await ConversationApi.show(id);
      // Mirrors the store's getConversation action, bypassed here because it
      // swallows the status codes this failure handling needs.
      store.commit('UPDATE_CONVERSATION', response.data);
      store.commit('contacts/SET_CONTACT_ITEM', response.data.meta.sender);
      failures.delete(id);
      markUnavailable(id, false);
    } catch (error) {
      const status = error?.response?.status;
      if (status === 404) {
        failure.notFound += 1;
        if (failure.notFound >= PRUNE_AFTER_NOT_FOUND) {
          // Two consecutive authoritative 404s = conversation deleted; prune
          // the pin. Anything less stays — a transient failure must never
          // destroy a deliberate pin.
          failures.delete(id);
          unpin(id);
          return;
        }
      }
      // 403 (access revoked) parks immediately — no retry until the pin set
      // changes; network/5xx retries up to the cap.
      failure.parked = status === 403;
      failure.count = failure.parked ? MAX_FETCH_FAILURES : failure.count + 1;
      failures.set(id, failure);
      if (failure.count >= MAX_FETCH_FAILURES) markUnavailable(id, true);
      else scheduleRetry();
    } finally {
      inFlight.delete(id);
    }
  };

  const hydrateMissing = () => missingIds.value.forEach(hydrate);
  const debouncedHydrate = useDebounceFn(hydrateMissing, 300);

  watch(retryTick, () => hydrateMissing());

  // Store evictions re-surface ids here; the debounce absorbs rapid filter
  // switches.
  watch(missingIds, ids => {
    if (ids.length) debouncedHydrate();
  });

  // A pin-set change re-arms EVERYTHING, parked 403s included (and covers
  // account switches, since pins are account-keyed).
  watch(
    () => pins.value.map(pin => pin.id).join(','),
    () => {
      resetFailures({ includeParked: true });
      debouncedHydrate();
    }
  );

  useEventListener(window, 'online', () => {
    resetFailures();
    debouncedHydrate();
  });

  // One deliberate retry of non-parked failures when the tab becomes visible.
  useEventListener(document, 'visibilitychange', () => {
    if (document.visibilityState !== 'visible') return;
    resetFailures();
    debouncedHydrate();
  });

  hydrateMissing();
}
